import { DepartmentNode } from 'features/common/models';
import { COLLAPSE_ALL, EXPAND_ALL } from './constants';
import {
  ChildSubMenu,
  ChildSubMenuGroup,
  Course,
  EXPAND_SETTING,
  Genre,
  Menu,
  MenuBook,
  MenuDetail,
  SubGenre,
  SubMenu,
  SubMenuGroup,
} from './models';

export function getIsExpandStatus(menuBook: MenuBook, editedMenuBookIds: number[], isExpand?: EXPAND_SETTING) {
  let isOpenDefault = true;

  if (isExpand === EXPAND_ALL) {
    isOpenDefault = true;
  } else if (isExpand === COLLAPSE_ALL) {
    isOpenDefault = false;
  } else {
    isOpenDefault = editedMenuBookIds.includes(menuBook.id);
  }

  return isOpenDefault;
}

export function getBackgroundColorByClassification(classification: string | null) {
  let color: string = '#999';

  switch (classification) {
    case null:
      color = '#4D4D4D';
      break;
    case '':
      color = '#4D4D4D';
      break;
    case 'A':
      color = '#666';
      break;
    case 'B':
      color = '#808080';
      break;
    case 'U':
      color = '#999';
      break;
    default:
      color = '#4D4D4D';
      break;
  }

  return color;
}

export function getPaddingByClassification(department: DepartmentNode) {
  const level = department.hierarchy_level > 0 ? department.hierarchy_level - 1 : 0;

  return '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'.repeat(level);
}

export function getDefaultMobileOrderDisplayFlg(menuDetail: MenuDetail) {
  if (menuDetail) {
    let mobileOrderDisplayFlg = false;

    // check mobile_order_display_by_counter_pattern (mobile_order_display_flg)
    if (menuDetail.counter_pattern_display_flg === null || menuDetail.counter_pattern_display_flg === undefined) {
      return undefined;
    }

    mobileOrderDisplayFlg = menuDetail.counter_pattern_display_flg;

    // check mobile_order_display_by_restaurant (mobile_order_display_flg)
    if (menuDetail.restaurant_display_flg !== null && menuDetail.restaurant_display_flg !== undefined) {
      mobileOrderDisplayFlg = menuDetail.restaurant_display_flg;
    }

    return mobileOrderDisplayFlg;
  }

  return undefined;
}

export function hasEditedMobileOrderDisplayFlg(menuDetail: MenuDetail) {
  if (menuDetail) {
    return (
      menuDetail.counter_pattern_display_flg != null &&
      menuDetail.restaurant_display_flg != null &&
      menuDetail.counter_pattern_display_flg !== menuDetail.restaurant_display_flg
    );
  }

  return false;
}

export function countMultiplePosCodeRegisteredOnMenu(menu: Menu) {
  const assignedMenuDetail = menu.menu_details.filter((menuDetail) => menuDetail.counter_pattern_display_flg !== null);
  const posCodeCds = assignedMenuDetail.map((menuDetail) => Number(menuDetail.pos_code_cd));
  const uniquePosCodeCds = posCodeCds.filter((value, index, array) => array.indexOf(value) === index);

  return uniquePosCodeCds.length;
}

export function countMultiplePosCodeRegisteredOnSubMenu(subMenu: SubMenu) {
  const assignedMenuDetail = subMenu.menu_details.filter(
    (menuDetail) => menuDetail.counter_pattern_display_flg !== null
  );
  const posCodeCds = assignedMenuDetail.map((menuDetail) => Number(menuDetail.pos_code_cd));
  const uniquePosCodeCds = posCodeCds.filter((value, index, array) => array.indexOf(value) === index);

  return uniquePosCodeCds.length;
}

export function countMultiplePosCodeRegisteredOnChildSubMenu(childSubMenu: ChildSubMenu) {
  const assignedMenuDetail = childSubMenu.menu_details.filter(
    (menuDetail) => menuDetail.counter_pattern_display_flg !== null
  );
  const posCodeCds = assignedMenuDetail.map((menuDetail) => Number(menuDetail.pos_code_cd));
  const uniquePosCodeCds = posCodeCds.filter((value, index, array) => array.indexOf(value) === index);

  return uniquePosCodeCds.length;
}

export function countMenuHasEditedMObileOrderDisplayFlg(menu: Menu) {
  let count = 0;

  menu.menu_details.forEach(function (menuDetail) {
    if (hasEditedMobileOrderDisplayFlg(menuDetail)) {
      count += 1;
    }
  });

  menu.sub_menu_groups.forEach(function (subMenuGroup) {
    count += countSubMenuGroupHasEditedMObileOrderDisplayFlg(subMenuGroup);
  });

  return count;
}

export function countSubMenuHasEditedMObileOrderDisplayFlg(subMenu: SubMenu) {
  let count = 0;

  subMenu.menu_details.forEach(function (menuDetail) {
    if (hasEditedMobileOrderDisplayFlg(menuDetail)) {
      count += 1;
    }
  });

  subMenu.child_sub_menu_groups.forEach(function (childSubMenuGroup) {
    count += countChildSubMenuGroupHasEditedMObileOrderDisplayFlg(childSubMenuGroup);
  });

  return count;
}

export function countChildSubMenuHasEditedMObileOrderDisplayFlg(childSubMenu: ChildSubMenu) {
  let count = 0;

  childSubMenu.menu_details.forEach(function (menuDetail) {
    if (hasEditedMobileOrderDisplayFlg(menuDetail)) {
      count += 1;
    }
  });

  return count;
}

export function countSubMenuGroupHasEditedMObileOrderDisplayFlg(subMenuGroup: SubMenuGroup) {
  let count = 0;

  subMenuGroup.sub_menus.forEach(function (subMenu) {
    count += countSubMenuHasEditedMObileOrderDisplayFlg(subMenu);
  });

  return count;
}

export function countChildSubMenuGroupHasEditedMObileOrderDisplayFlg(childSubMenuGroup: ChildSubMenuGroup) {
  let count = 0;

  childSubMenuGroup.child_sub_menus.forEach(function (childSubMenu) {
    count += countChildSubMenuHasEditedMObileOrderDisplayFlg(childSubMenu);
  });

  return count;
}

export function countSubGenreHasEditedMObileOrderDisplayFlg(subgenre: SubGenre) {
  let count = 0;

  subgenre.menus.forEach(function (menu) {
    count += countMenuHasEditedMObileOrderDisplayFlg(menu);
  });

  return count;
}

export function countGenreHasEditedMObileOrderDisplayFlg(genre: Genre) {
  let count = 0;

  genre.subgenres.forEach(function (subgenre) {
    count += countSubGenreHasEditedMObileOrderDisplayFlg(subgenre);
  });

  return count;
}

export function countCourseHasEditedMObileOrderDisplayFlg(course: Course) {
  let count = 0;

  course.genres.forEach(function (genre) {
    count += countGenreHasEditedMObileOrderDisplayFlg(genre);
  });

  return count;
}

export function countMenuBookHasEditedMObileOrderDisplayFlg(menuBook: MenuBook) {
  let count = 0;

  menuBook.genres.forEach(function (genre) {
    count += countGenreHasEditedMObileOrderDisplayFlg(genre);
  });

  menuBook.courses.forEach(function (course) {
    count += countCourseHasEditedMObileOrderDisplayFlg(course);
  });

  return count;
}
